/**
 * Diff baris (LCS) — dipakai panel 🧠 Prompt untuk membandingkan versi prompt
 * antar waktu (snapshot deploy vs template aktif).
 *
 * MURNI TANPA impor (pola `lib/nlReport.ts`): modul ini dimuat probe Node
 * `--experimental-strip-types` dan juga diimpor `dashboard/src/App.tsx`.
 *
 * Kebutuhannya kecil dan deterministik: template prompt ±120 baris, jadi DP LCS
 * O(n·m) aman — tidak perlu library diff di dashboard.
 */

export type DiffOp = "same" | "add" | "del";

export interface DiffLine {
  op: DiffOp;
  text: string;
}

/**
 * Diff dua teks per BARIS. `a` = versi lama (snapshot), `b` = versi baru
 * (template aktif). `add` = baris hanya ada di versi baru, `del` = hanya ada di
 * versi lama, `same` = identik (dipertahankan di keduanya).
 *
 * Tie-break DP: `dp[i+1][j] >= dp[i][j+1]` → hapus dulu — deterministik, jadi
 * output sama di runtime Convex dan probe Node.
 */
export function diffLines(a: string, b: string): DiffLine[] {
  const x = String(a ?? "").split("\n");
  const y = String(b ?? "").split("\n");
  const m = x.length;
  const n = y.length;

  // dp[i][j] = panjang LCS x[i..] vs y[j..]
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) {
      out.push({ op: "same", text: x[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: "del", text: x[i] });
      i++;
    } else {
      out.push({ op: "add", text: y[j] });
      j++;
    }
  }
  while (i < m) out.push({ op: "del", text: x[i++] });
  while (j < n) out.push({ op: "add", text: y[j++] });
  return out;
}

/** Ringkasan jumlah baris per op (ditampilkan di panel: ± add/del). */
export function diffSummary(lines: readonly DiffLine[]): { same: number; add: number; del: number } {
  const summary = { same: 0, add: 0, del: 0 };
  for (const line of lines) summary[line.op]++;
  return summary;
}
